import React, { useState, useEffect } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Details, Update, List } from "../../services/report.services";
import { List as CategoryList } from "../../services/pujaCategory.services";
import CustomCkeditor from "../../common/customeditor";
import Loader from "../../common/loader";

const EditReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewThumbnail, setPreviewThumbnail] = useState(null);
    const [existingThumbnail, setExistingThumbnail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [assignedCategoryIds, setAssignedCategoryIds] = useState([]);
 
    useEffect(() => {
        const formData = new FormData();
        formData.append("search", JSON.stringify({ type: "expertise", status: 1 }));
        CategoryList(formData).then((res) => {
            if (res.success) {
                setCategories(res.data.list || []);
            }
        });

        const reportForm = new FormData();
        reportForm.append("page", 1);
        reportForm.append("per_page", 1000);
        List(reportForm).then((res) => {
            if ((res.status || res.success) && res.data?.list) {
                const assignedIds = res.data.list
                    .map(r => r.category_id)
                    .filter(id => id !== null && id !== undefined);
                setAssignedCategoryIds(assignedIds);
            }
        }).catch(err => console.log("Error fetching reports:", err));
    }, []);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Reports", url: "/admin/report/list/1" },
        { title: t("link_edit"), url: "" },
    ];

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required").max(255, "Title must be at most 255 characters"),
        slug: Yup.string().trim().required("Slug is required").max(255, "Slug must be at most 255 characters").matches(/^[a-z0-9-]+$/, "Slug must only contain lowercase letters, numbers, and hyphens"),
        short_description: Yup.string().trim().required("Description is required").max(1000, "Description must be at most 1000 characters"),
        detailed_description: Yup.string().trim().required("Long description is required"),
        personalized_price_inr: Yup.number().required("Personalized Price (INR) is required").min(0, "Price cannot be negative"),
        personalized_price_usd: Yup.number().required("Personalized Price (USD) is required").min(0, "Price cannot be negative"),
        ai_price_inr: Yup.number().required("AI Price (INR) is required").min(0, "Price cannot be negative"),
        ai_price_usd: Yup.number().required("AI Price (USD) is required").min(0, "Price cannot be negative"),
        gst_percent: Yup.number().typeError("GST percent must be a number").optional().min(0, "Price cannot be negative").max(100, "GST percent cannot exceed 100"),
        category_id: Yup.string().required("Category is required"),
        parameters: Yup.array().of(Yup.string().trim().required("Parameter title is required")),
        questions: Yup.array().when("is_ask_question", {
            is: true,
            then: (schema) => schema.of(
                Yup.object().shape({
                    question_length: Yup.number().typeError("Length must be a number").required("Length is required").min(1, "Min 1").integer("Must be an integer"),
                    question_quantity: Yup.number().required("Quantity is required").min(1, "Min 1"),
                    price_inr: Yup.number().required("Price (INR) is required").min(0, "Min 0"),
                    price_usd: Yup.number().required("Price (USD) is required").min(0, "Min 0"),
                })
            ),
            otherwise: (schema) => schema.notRequired()
        }),
        report_type: Yup.string().required("Report Type is required"),
        form_type: Yup.string().required("Form Type is required"),
        first_form_title: Yup.string().trim().required("First Form Title is required"),
        second_form_title: Yup.string().when("form_type", {
            is: "double",
            then: (schema) => schema.trim().required("Second Form Title is required"),
            otherwise: (schema) => schema.notRequired()
        }),
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            slug: "",
            short_description: "",
            detailed_description: "",
            personalized_price_inr: "",
            personalized_price_usd: "",
            ai_price_inr: "",
            ai_price_usd: "",
            gst_percent: 0,
            status: "Active",
            category_id: "",
            report_image: null,
            thumbnail_image: null,
            old_image: "",
            report_type: "Personalized",
            form_type: "single",
            first_form_title: "",
            second_form_title: "",
            parameters: [""],
            questions: [{ question_length: "", question_quantity: "", price_inr: "", price_usd: "" }],
            is_instant: false,
            is_ask_question: false,
            is_premium: false,
            sort_order: 0,
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("id", id);
            formData.append("title", values.title);
            formData.append("slug", values.slug);
            formData.append("short_description", values.short_description);
            formData.append("detailed_description", values.detailed_description);
            formData.append("personalized_price_inr", values.personalized_price_inr);
            formData.append("personalized_price_usd", values.personalized_price_usd);
            formData.append("ai_price_inr", values.ai_price_inr);
            formData.append("ai_price_usd", values.ai_price_usd);
            formData.append("gst_percent", values.gst_percent || 0);
            formData.append("status", values.status);
            formData.append("category_id", values.category_id);
            formData.append("report_type", values.report_type);
            formData.append("form_type", values.form_type);
            formData.append("first_form_title", values.first_form_title);
            formData.append("second_form_title", values.second_form_title);
            formData.append("is_instant", values.is_instant ? 1 : 0);
            formData.append("is_ask_question", values.is_ask_question ? 1 : 0);
            formData.append("is_premium", values.is_premium ? 1 : 0);
            formData.append("sort_order", values.sort_order);

            const filteredParameters = values.parameters.filter(p => p.trim() !== "");
            formData.append("parameters", JSON.stringify(filteredParameters));

            const filteredQuestions = values.is_ask_question
                ? values.questions.filter(q => String(q.question_length).trim() !== "")
                : [];
            formData.append("questions", JSON.stringify(filteredQuestions));

            if (values.report_image) {
                formData.append("report_image", values.report_image);
            }
            if (values.thumbnail_image) {
                formData.append("thumbnail_image", values.thumbnail_image);
            }

            Update(formData)
                .then((response) => {
                    if (response.status || response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/report/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    useEffect(() => {
        if (id) {
            Details(id)
                .then((response) => {
                    if ((response.status || response.success) && response.data) {
                        const data = response.data;
                        formik.setValues({
                            title: data.title || "",
                            slug: data.slug || "",
                            short_description: data.short_description || "",
                            detailed_description: data.detailed_description || "",
                            personalized_price_inr: data.personalized_price_inr || "",
                            personalized_price_usd: data.personalized_price_usd || "",
                            ai_price_inr: data.ai_price_inr || "",
                            ai_price_usd: data.ai_price_usd || "",
                            gst_percent: data.gst_percent || 0,
                            status: data.status ? data.status : "Active",
                            category_id: data.category_id || "",
                            report_type: data.report_type || "Personalized",
                            form_type: data.form_type || "single",
                            first_form_title: data.first_form_title || "",
                            second_form_title: data.second_form_title || "",
                            report_image: null,
                            thumbnail_image: null,
                            old_image: data.report_image || "",
                            parameters: data.parameters && data.parameters.length > 0 
                                ? data.parameters.map(p => p.parameter_title) 
                                : [""],
                            questions: data.questions && data.questions.length > 0 
                                ? data.questions.map(q => ({
                                    question_length: q.question_length || "",
                                    question_quantity: q.question_quantity || 1,
                                    price_inr: q.price_inr || 0,
                                    price_usd: q.price_usd || 0
                                })) 
                                : [{ question_length: "", question_quantity: "", price_inr: "", price_usd: "" }],
                            is_instant: data.is_instant === 1 || data.is_instant === true,
                            is_ask_question: data.is_ask_question === 1 || data.is_ask_question === true,
                            is_premium: data.is_premium === 1 || data.is_premium === true,
                            sort_order: data.sort_order || 0,
                        });

                        if (data.report_image) {
                            const imageUrl = data.report_image.startsWith("http") 
                                ? data.report_image 
                                : `${process.env.REACT_APP_API_URL || ""}/${data.report_image}`;
                            setPreviewImage(imageUrl);
                        }
                        if (data.thumbnail_image) {
                            const thumbUrl = data.thumbnail_image.startsWith("http") 
                                ? data.thumbnail_image 
                                : `${process.env.REACT_APP_API_URL || ""}/${data.thumbnail_image}`;
                            setExistingThumbnail(thumbUrl);
                        }
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("report_image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewImage(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("thumbnail_image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewThumbnail(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const currentThumbnailSrc = previewThumbnail || existingThumbnail;

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Report</h6>
                        <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter report title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Slug */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="slug" className="text-left d-flex">
                                        Slug (Report Id):<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="slug"
                                        id="slug"
                                        type="text"
                                        className="form-control"
                                        placeholder="enter-report-slug"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.slug}
                                        readOnly
                                    />
                                    {formik.errors.slug && formik.touched.slug && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.slug}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value="Active">{t("Active")}</option>
                                        <option value="Inactive">{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Category */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="category_id" className="text-left d-flex">
                                        Category :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="category_id"
                                        id="category_id"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.category_id}
                                    >
                                        <option value="">Select Category</option>
                                        {categories
                                            .filter(cat => !assignedCategoryIds.includes(cat.id) || cat.id == formik.values.category_id)
                                            .map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.title}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    {formik.errors.category_id && formik.touched.category_id && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.category_id}
                                        </span>
                                    )}
                                </div>

                                {/* Sort Order */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="sort_order" className="text-left d-flex">
                                        Sort Order / Priority :
                                    </label>
                                    <input
                                        name="sort_order"
                                        id="sort_order"
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        placeholder="Enter sort order (e.g. 1, 2, 3)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.sort_order}
                                    />
                                </div>

                                {/* Price INR */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="personalized_price_inr" className="text-left d-flex">
                                        Personalized Price (INR) :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="personalized_price_inr"
                                        id="personalized_price_inr"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.personalized_price_inr}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.personalized_price_inr && formik.touched.personalized_price_inr && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.personalized_price_inr}
                                        </span>
                                    )}
                                </div>

                                {/* Price USD */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="personalized_price_usd" className="text-left d-flex">
                                        Personalized Price (USD) :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="personalized_price_usd"
                                        id="personalized_price_usd"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.personalized_price_usd}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.personalized_price_usd && formik.touched.personalized_price_usd && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.personalized_price_usd}
                                        </span>
                                    )}
                                </div>

                                {/* AI Price INR */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="ai_price_inr" className="text-left d-flex">
                                        AI Price (INR) :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="ai_price_inr"
                                        id="ai_price_inr"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.ai_price_inr}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.ai_price_inr && formik.touched.ai_price_inr && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.ai_price_inr}
                                        </span>
                                    )}
                                </div>

                                {/* AI Price USD */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="ai_price_usd" className="text-left d-flex">
                                        AI Price (USD) :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="ai_price_usd"
                                        id="ai_price_usd"
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.ai_price_usd}
                                        onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                                    />
                                    {formik.errors.ai_price_usd && formik.touched.ai_price_usd && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.ai_price_usd}
                                        </span>
                                    )}
                                </div>

                                {/* GST Percent */}
                                <div className="col-lg-3 form-group">
                                    <label htmlFor="gst_percent" className="text-left d-flex">
                                        GST (%) (India Only) :
                                    </label>
                                    <input
                                        name="gst_percent"
                                        id="gst_percent"
                                        type="number"
                                        className="form-control"
                                        placeholder="0"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.gst_percent}
                                    />
                                    {formik.errors.gst_percent && formik.touched.gst_percent && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.gst_percent}
                                        </span>
                                    )}
                                </div>

                                {/* Report Type */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="report_type" className="text-left d-flex">
                                        Report Type :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="report_type"
                                        id="report_type"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.report_type}
                                    >
                                        <option value="Personalized">Personalized</option>
                                        <option value="Instant">Instant</option>
                                        <option value="Both">Both</option>
                                    </select>
                                    {formik.errors.report_type && formik.touched.report_type && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.report_type}
                                        </span>
                                    )}
                                </div>

                                {/* Form Type */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="form_type" className="text-left d-flex">
                                        Form Type :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="form_type"
                                        id="form_type"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.form_type}
                                    >
                                        <option value="single">Single</option>
                                        <option value="double">Double</option>
                                    </select>
                                    {formik.errors.form_type && formik.touched.form_type && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.form_type}
                                        </span>
                                    )}
                                </div>

                                {/* Checkboxes for Flags */}
                                <div className="col-lg-12 form-group mt-2 mb-3">
                                    <div className="row">
                                        <div className="col-lg-4 col-md-4 col-sm-12 d-flex align-items-center form-check" style={{ paddingLeft: "15px" }}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="is_instant"
                                                name="is_instant"
                                                checked={formik.values.is_instant}
                                                onChange={formik.handleChange}
                                                style={{ width: "20px", height: "20px", cursor: "pointer", position: "relative", marginLeft: "0" }}
                                            />
                                            <label className="form-check-label ms-2 mb-0" htmlFor="is_instant" style={{ cursor: "pointer", fontWeight: "500", marginLeft: "10px" }}>
                                                Instant Report
                                            </label>
                                        </div>
                                        <div className="col-lg-4 col-md-4 col-sm-12 d-flex align-items-center form-check" style={{ paddingLeft: "15px" }}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="is_ask_question"
                                                name="is_ask_question"
                                                checked={formik.values.is_ask_question}
                                                onChange={formik.handleChange}
                                                style={{ width: "20px", height: "20px", cursor: "pointer", position: "relative", marginLeft: "0" }}
                                            />
                                            <label className="form-check-label ms-2 mb-0" htmlFor="is_ask_question" style={{ cursor: "pointer", fontWeight: "500", marginLeft: "10px" }}>
                                                Ask a Question
                                            </label>
                                        </div>
                                        <div className="col-lg-4 col-md-4 col-sm-12 d-flex align-items-center form-check" style={{ paddingLeft: "15px" }}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="is_premium"
                                                name="is_premium"
                                                checked={formik.values.is_premium}
                                                onChange={formik.handleChange}
                                                style={{ width: "20px", height: "20px", cursor: "pointer", position: "relative", marginLeft: "0" }}
                                            />
                                            <label className="form-check-label ms-2 mb-0" htmlFor="is_premium" style={{ cursor: "pointer", fontWeight: "500", marginLeft: "10px" }}>
                                                Personalized Report
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* First Form Title */}
                                <div className={"col-lg-6 form-group"}>
                                    <label htmlFor="first_form_title" className="text-left d-flex">
                                        First Form Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="first_form_title"
                                        id="first_form_title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter First Form Title (e.g. Boy Details)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.first_form_title}
                                    />
                                    {formik.errors.first_form_title && formik.touched.first_form_title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.first_form_title}
                                        </span>
                                    )}
                                </div>

                                {/* Second Form Title */}
                                {formik.values.form_type === 'double' && (
                                    <div className="col-lg-6 form-group">
                                        <label htmlFor="second_form_title" className="text-left d-flex">
                                            Second Form Title :<span className="requirestar">*</span>
                                        </label>
                                        <input
                                            name="second_form_title"
                                            id="second_form_title"
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Second Form Title (e.g. Girl Details)"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.second_form_title}
                                        />
                                        {formik.errors.second_form_title && formik.touched.second_form_title && (
                                            <span className="text-danger d-flex text-left">
                                                {formik.errors.second_form_title}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Image */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="report_image" className="text-left d-flex">
                                        Report Image :
                                    </label>
                                    <input
                                        name="report_image"
                                        id="report_image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {previewImage && (
                                        <div className="mt-2 text-left">
                                            <img
                                                src={previewImage}
                                                alt="Preview"
                                                style={{
                                                    width: "100px",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #ddd",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Thumbnail Image */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="thumbnail_image" className="text-left d-flex">
                                        Thumbnail Image :
                                    </label>
                                    <input
                                        name="thumbnail_image"
                                        id="thumbnail_image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleThumbnailChange}
                                        onBlur={() => formik.setFieldTouched("thumbnail_image", true)}
                                    />
                                    {currentThumbnailSrc && (
                                        <div className="mt-2 text-left">
                                            <img
                                                src={currentThumbnailSrc}
                                                alt="Thumbnail Preview"
                                                style={{
                                                    width: "100px",
                                                    height: "100px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #ddd",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="short_description" className="text-left d-flex">
                                        Short Description :<span className="requirestar">*</span>
                                    </label>
                                    <textarea
                                        name="short_description"
                                        id="short_description"
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter report description"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.short_description}
                                    ></textarea>
                                    {formik.errors.short_description && formik.touched.short_description && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.short_description}
                                        </span>
                                    )}
                                </div>

                                {/* CKEditor Long Description */}
                                <div className="col-lg-12 form-group">
                                    <label htmlFor="detailed_description" className="text-left d-flex cp">
                                        Detailed Description :<span className="requirestar">*</span>
                                    </label>
                                    <CustomCkeditor 
                                        fieldname={"detailed_description"} 
                                        setFieldValue={formik.setFieldValue} 
                                        value={formik.values.detailed_description} 
                                        setFieldTouched={formik.setFieldTouched} 
                                    />
                                    <span className="text-danger d-flex text-left">
                                        {formik.errors.detailed_description && formik.touched.detailed_description && formik.errors.detailed_description}
                                    </span>
                                </div>

                                {/* Report Parameters */}
                                <div className="col-lg-12 form-group mt-3">
                                    <label className="text-left d-flex">Report Parameters :</label>
                                    <FieldArray
                                        name="parameters"
                                        render={(arrayHelpers) => (
                                            <div>
                                                {formik.values.parameters.map((param, index) => (
                                                    <div key={index} className="mb-2">
                                                        <div className="row row-sm align-items-center">
                                                            <div className="col-lg-10">
                                                                <input
                                                                    name={`parameters.${index}`}
                                                                    className="form-control"
                                                                    placeholder="Enter parameter"
                                                                    value={param}
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                />
                                                            </div>
                                                            <div className="col-lg-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                    disabled={formik.values.parameters.length === 1}
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {formik.errors.parameters && formik.errors.parameters[index] && formik.touched.parameters && formik.touched.parameters[index] && (
                                                            <div className="row row-sm">
                                                                <div className="col-lg-10">
                                                                    <span className="text-danger d-flex text-left">
                                                                        {formik.errors.parameters[index]}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm mt-1"
                                                    onClick={() => arrayHelpers.push("")}
                                                >
                                                    <i className="fa fa-plus me-1"></i> Add More
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>

                                {/* Report Questions */}
                                {formik.values.is_ask_question && (
                                    <div className="col-lg-12 form-group mt-3">
                                        <label className="text-left d-flex fw-bold">Report Questions & Prices :</label>
                                        <FieldArray
                                            name="questions"
                                            render={(arrayHelpers) => (
                                                <div>
                                                    <div className="row row-sm mb-1 align-items-center">
                                                        <div className="col-lg-10">
                                                            <div className="row w-100 m-0">
                                                                <div className="col-lg-4 px-1">
                                                                    <label className="form-label text-left d-flex fw-semibold mb-1" style={{ fontSize: "13px" }}>Max Words</label>
                                                                </div>
                                                                <div className="col-lg-2 px-1">
                                                                    <label className="form-label text-left d-flex fw-semibold mb-1" style={{ fontSize: "13px" }}>Year</label>
                                                                </div>
                                                                <div className="col-lg-3 px-1">
                                                                    <label className="form-label text-left d-flex fw-semibold mb-1" style={{ fontSize: "13px" }}>Price (INR)</label>
                                                                </div>
                                                                <div className="col-lg-3 px-1">
                                                                    <label className="form-label text-left d-flex fw-semibold mb-1" style={{ fontSize: "13px" }}>Price (USD)</label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-lg-2"></div>
                                                    </div>

                                                    {formik.values.questions.map((q, index) => (
                                                        <div key={index} className="row row-sm mb-2 align-items-center">
                                                            <div className="col-lg-10">
                                                                <div className="row w-100 m-0">
                                                                    <div className="col-lg-4 px-1 mb-2">
                                                                        <input name={`questions.${index}.question_length`} type="number" className="form-control" placeholder="Max Words" value={q.question_length} onChange={formik.handleChange} onBlur={formik.handleBlur} onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()} />
                                                                        {formik.errors.questions?.[index]?.question_length && formik.touched.questions?.[index]?.question_length && (<span className="text-danger d-flex text-left mt-1">{formik.errors.questions[index].question_length}</span>)}
                                                                    </div>
                                                                    <div className="col-lg-2 px-1 mb-2">
                                                                        <input name={`questions.${index}.question_quantity`} type="number" className="form-control" placeholder="Year" value={q.question_quantity} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                                        {formik.errors.questions?.[index]?.question_quantity && formik.touched.questions?.[index]?.question_quantity && (<span className="text-danger d-flex text-left mt-1">{formik.errors.questions[index].question_quantity}</span>)}
                                                                    </div>
                                                                    <div className="col-lg-3 px-1 mb-2">
                                                                        <input name={`questions.${index}.price_inr`} type="number" className="form-control" placeholder="Price (INR)" value={q.price_inr} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                                        {formik.errors.questions?.[index]?.price_inr && formik.touched.questions?.[index]?.price_inr && (<span className="text-danger d-flex text-left mt-1">{formik.errors.questions[index].price_inr}</span>)}
                                                                    </div>
                                                                    <div className="col-lg-3 px-1 mb-2">
                                                                        <input name={`questions.${index}.price_usd`} type="number" className="form-control" placeholder="Price (USD)" value={q.price_usd} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                                        {formik.errors.questions?.[index]?.price_usd && formik.touched.questions?.[index]?.price_usd && (<span className="text-danger d-flex text-left mt-1">{formik.errors.questions[index].price_usd}</span>)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                    disabled={formik.values.questions.length === 1}
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm mt-1"
                                                        onClick={() => arrayHelpers.push({ question: "", question_quantity: "", price_inr: "", price_usd: "" })}
                                                    >
                                                        <i className="fa fa-plus me-1"></i> Add More
                                                    </button>
                                                </div>
                                            )}
                                        />
                                    </div>
                                )}

                                <div className="col-md-12 mt-3">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                        </FormikProvider>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditReport;
